import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../scss/falling_icons_background.scss";

interface FallingIconsBackgroundProps {
    imageSrc: string;
    imageAlt?: string;
    count?: number;
    className?: string;
}

interface FallingIconInstance {
    id: number;
    left: number;
    size: number;
    duration: number;
    opacity: number;
    drift: number;
    blur: number;
    rotate: number;
}

const FallingIconsBackground: React.FC<FallingIconsBackgroundProps> = ({
    imageSrc,
    imageAlt = "",
    count = 9,
    className = "",
}) => {
    const [instances, setInstances] = useState<FallingIconInstance[]>([]);
    const nextIdRef = useRef(1);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isMobile = useMemo(
        () => window.matchMedia("(max-width: 768px)").matches,
        []
    );

    const createInstance = (): FallingIconInstance => ({
        id: nextIdRef.current++,
        left: Math.round((4 + Math.random() * 92) * 100) / 100,
        size: Math.round((isMobile ? 24 : 30) + Math.random() * (isMobile ? 28 : 54)),
        duration: Math.round((isMobile ? 13 : 16) + Math.random() * (isMobile ? 10 : 14)),
        opacity: Math.round((0.06 + Math.random() * 0.1) * 100) / 100,
        drift: Math.round((-28 + Math.random() * 56) * 10) / 10,
        blur: Math.round(Math.random() * (isMobile ? 0.5 : 0.8) * 10) / 10,
        rotate: Math.round((-18 + Math.random() * 36) * 10) / 10,
    });

    useEffect(() => {
        const initialCount = Math.max(4, Math.min(count, isMobile ? 6 : 8));
        setInstances(Array.from({ length: initialCount }, () => createInstance()));

        const scheduleNext = () => {
            timeoutRef.current = setTimeout(
                () => {
                    setInstances((prev) => {
                        if (prev.length >= count) return prev;
                        return [...prev, createInstance()];
                    });
                    scheduleNext();
                },
                Math.round((isMobile ? 900 : 700) + Math.random() * (isMobile ? 1200 : 900))
            );
        };

        scheduleNext();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [count, isMobile]);

    const handleAnimationEnd = (id: number) => {
        setInstances((prev) => {
            const next = prev.filter((item) => item.id !== id);
            if (next.length >= count) return next;
            return [...next, createInstance()];
        });
    };

    return (
        <div
            className={`falling-icons-bg ${className}`.trim()}
            aria-hidden="true"
        >
            {instances.map((icon) => (
                <span
                    key={`${imageSrc}-${icon.id}`}
                    className="falling-icons-bg__item"
                    onAnimationEnd={() => handleAnimationEnd(icon.id)}
                    style={
                        {
                            "--icon-left": `${icon.left}%`,
                            "--icon-size": `${icon.size}px`,
                            "--icon-duration": `${icon.duration}s`,
                            "--icon-opacity": icon.opacity,
                            "--icon-drift": `${icon.drift}px`,
                            "--icon-blur": `${icon.blur}px`,
                            "--icon-rotate": `${icon.rotate}deg`,
                        } as React.CSSProperties
                    }
                >
                    <img src={imageSrc} alt={imageAlt} loading="lazy" />
                </span>
            ))}
        </div>
    );
};

export default FallingIconsBackground;
